import moment from 'moment';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../../helpers/api';
import {
  ROLE_ADMIN,
  ROLE_SALES,
  ROLE_SALES_AND_INVENTORY_MANAGER,
  ROLE_SALES_MANAGER,
  ROLE_SUPER_ADMIN
} from '../../helpers/constants';
import { getProfileFromLocalStorage, getSalesRepOfSalesManager } from '../../helpers/session';
import { userHasAnyRole } from '../../helpers/roleHelpers';

export const firstName = () => {
  const name = localStorage.getItem('user-name') || '';
  return name.split(' ')[0] || 'there';
};

export const greeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

export const lastContactLabel = contact => {
  if (!contact?.lastContactDate) return contact ? 'Never' : '';
  const days = moment().startOf('day').diff(moment(contact.lastContactDate).startOf('day'), 'days');
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
};

const storedJson = key => {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (e) {
    return null;
  }
};

const allowedStoreIds = () => {
  if (userHasAnyRole(ROLE_SUPER_ADMIN)) return null;
  const many = storedJson('user-sales-locations');
  const ids = (Array.isArray(many) ? many : [])
    .map(loc => (loc && (loc._id || loc)) || null)
    .filter(Boolean)
    .map(String);
  if (ids.length) return ids;
  const one = storedJson('user-sales-location');
  const id = one && (one._id || one);
  return id ? [String(id)] : null;
};

const memberStoreIds = person => {
  const ids = (person?.salesLocations || [])
    .map(loc => (loc && (loc._id || loc)) || null)
    .filter(Boolean)
    .map(String);
  const home = person?.salesLocation && (person.salesLocation._id || person.salesLocation);
  if (home) ids.push(String(home));
  return [...new Set(ids)];
};

const rolesOf = person =>
  [...(Array.isArray(person?.roles) ? person.roles : []), person?.role]
    .filter(Boolean)
    .map(role => String(role).toLowerCase());

const salesRole = person =>
  rolesOf(person).some(role =>
    [ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_SALES, ROLE_SALES_MANAGER, ROLE_SALES_AND_INVENTORY_MANAGER]
      .map(item => item.toLowerCase())
      .includes(role)
  );

export const canPinBook = () => userHasAnyRole(ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_SALES_MANAGER);

export const pinOptions = appChoices => {
  const stores = appChoices.find(choice => choice.key === 'salesLocation')?.values || [];
  const allowed = allowedStoreIds();
  const visibleStores = (allowed
    ? stores.filter(store => allowed.includes(String(store._id)))
    : stores
  ).filter(store => store?._id && store.location);

  const members = appChoices.find(choice => choice.key === 'members')?.values || [];
  let people;
  if (userHasAnyRole(ROLE_SUPER_ADMIN, ROLE_ADMIN)) {
    people = members.filter(person => person?.name && person.enabled !== false && salesRole(person));
  } else {
    const me = getProfileFromLocalStorage() || {};
    const reps = getSalesRepOfSalesManager() || [];
    const allowedIds = new Set([me._id, ...reps.map(rep => rep?._id)].filter(Boolean).map(String));
    const fromMembers = members.filter(person => allowedIds.has(String(person._id)));
    const found = new Set(fromMembers.map(person => String(person._id)));
    const extras = [me, ...reps].filter(person => person?._id && person.name && !found.has(String(person._id)));
    people = [...fromMembers, ...extras];
  }

  const seen = new Set();
  const users = people
    .map(person => ({
      _id: person._id,
      name: person.name,
      salesLocations: memberStoreIds(person)
    }))
    .filter(person => {
      const id = String(person._id);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const names = new Map(members.filter(person => person?._id && person.name).map(person => [String(person._id), person.name]));
  const managers = members
    .filter(
      person =>
        person?.name &&
        person.enabled !== false &&
        rolesOf(person).includes(ROLE_SALES_MANAGER.toLowerCase())
    )
    .map(person => ({
      _id: person._id,
      name: person.name,
      salesLocations: memberStoreIds(person),
      reps: (person.salesReps || [])
        .map(rep => {
          const id = String((rep && (rep._id || rep)) || '');
          const name = names.get(id);
          return id && name ? { _id: id, name } : null;
        })
        .filter(Boolean)
    }))
    .filter(manager => !allowed || manager.salesLocations.some(id => allowed.includes(id)))
    .sort((a, b) => a.name.localeCompare(b.name));

  return { stores: visibleStores, users, managers };
};

export const choiceValues = (appChoices, key) =>
  (appChoices.find(choice => choice.key === key)?.values || [])
    .map(value => (typeof value === 'string' ? value : value?.name || value?.value || ''))
    .map(value => String(value).trim())
    .filter(Boolean);

export const boardRequest = async (endpoint, scopeQuery, params) => {
  const search = new URLSearchParams(String(scopeQuery || '').replace(/^\?/, ''));
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if ((key === 'fresh' || key === 'imported' || key === 'contracted') && String(value) === '0') return;
    search.set(key, String(value));
  });
  const query = search.toString();
  const { response, error } = await makeApiRequests({
    endpoint: endpoint + (query ? `?${query}` : ''),
    method: 'GET'
  });
  if (error) {
    toast.error(error);
    return null;
  }
  return response;
};

export const postToday = async (endpoint, requestBody) => {
  const { response, error } = await makeApiRequests({
    endpoint,
    method: 'POST',
    requestBody
  });
  if (error) {
    toast.error(error);
    return null;
  }
  return response ?? {};
};
